import React, { useState, useEffect, useMemo } from 'react';
import { 
    collection, 
    query, 
    orderBy, 
    onSnapshot, 
    doc, 
    addDoc, 
    updateDoc, 
    deleteDoc, 
    serverTimestamp,
    where,
    limit,
    getDocs
} from 'firebase/firestore';
import { GEMINI_API_KEY } from '../../config/firebase';

const PlannerIcons = {
  Check: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
  Trash: () => <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>,
  Document: () => <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
};

export default function Checklist({ user, db, navigateTo, eventId }) { // Updated props to include eventId
    const [tasks, setTasks] = useState([]);
    const [newTask, setNewTask] = useState('');
    const [category, setCategory] = useState('General');
    const [priority, setPriority] = useState('Medium');
    const [dueDate, setDueDate] = useState('');
    const [reminderDate, setReminderDate] = useState(''); // NEW: Reminder state
    const [filter, setFilter] = useState('all'); // all, active, completed
    const [suggestingTaskId, setSuggestingTaskId] = useState(null);
    const [findingTaskId, setFindingTaskId] = useState(null); // NEW: Track search state
    const [editingTask, setEditingTask] = useState(null); // NEW: Track editing state
    const [assignedTo, setAssignedTo] = useState(''); // Track assignment

    const categories = ['General', 'Venue', 'Food & Drink', 'Guests', 'Decor', 'Music', 'Budget', 'Logistics'];

    // Construct path dynamically based on eventId
    const collectionPath = useMemo(() => {
        return eventId 
            ? `users/${user.uid}/events/${eventId}/checklist` 
            : `users/${user.uid}/checklist`;
    }, [user.uid, eventId]);

    useEffect(() => {
        if (!user) return;
        const q = query(collection(db, collectionPath), orderBy("createdAt", "desc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedTasks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            // Client-side sort: Active first, then by Due Date (sooner first), then by Priority
            fetchedTasks.sort((a, b) => {
                if (a.completed !== b.completed) return a.completed ? 1 : -1;
                
                // Sort by date (sooner first). Items without a date go below items with dates.
                if (a.dueDate && b.dueDate) {
                    const dateCompare = a.dueDate.localeCompare(b.dueDate);
                    if (dateCompare !== 0) return dateCompare;
                } else if (a.dueDate && !b.dueDate) {
                    return -1;
                } else if (!a.dueDate && b.dueDate) {
                    return 1;
                }

                // Fallback to priority if dates are equal or both null
                const pMap = { High: 3, Medium: 2, Low: 1 };
                return (pMap[b.priority] || 0) - (pMap[a.priority] || 0);
            });
            setTasks(fetchedTasks);
        }, (error) => {
            if (error.code !== 'permission-denied') console.error("Checklist listener error:", error);
        });
        return () => unsubscribe();
    }, [user, db, collectionPath]);

    const addTask = async (e) => {
        e.preventDefault();
        if (!newTask.trim()) return;
        await addDoc(collection(db, collectionPath), {
            text: newTask,
            completed: false,
            category,
            priority,
            dueDate,
            reminderDate, // NEW: Save reminder date
            assignedTo,
            createdAt: serverTimestamp()
        });
        setNewTask('');
        setCategory('General');
        setPriority('Medium');
        setDueDate('');
        setReminderDate(''); // Reset reminder
        setAssignedTo('');
    };

    // NEW: Save Edited Task
    const saveEdit = async () => {
        if (!editingTask || !editingTask.text.trim()) return;
        try {
            await updateDoc(doc(db, collectionPath, editingTask.id), {
                text: editingTask.text,
                category: editingTask.category,
                priority: editingTask.priority,
                dueDate: editingTask.dueDate,
                reminderDate: editingTask.reminderDate || '', // NEW: Update reminder date
                assignedTo: editingTask.assignedTo || ''
            });
            setEditingTask(null);
        } catch (err) {
            console.error("Failed to save task", err);
        }
    };

    const toggleTask = async (task) => {
        await updateDoc(doc(db, collectionPath, task.id), {
            completed: !task.completed
        });
    };

    const deleteTask = async (e, taskId) => {
        e.preventDefault();
        e.stopPropagation();
        if(!window.confirm("Delete this task?")) return;
        try {
            await deleteDoc(doc(db, collectionPath, taskId));
        } catch (error) {
            console.error("Error deleting task:", error);
        }
    };

    // NEW: Function to get AI suggestions
    const getSuggestions = async (e, task) => {
        e.preventDefault();
        e.stopPropagation();
        setSuggestingTaskId(task.id);

        try {
            // Attempt to get location from user profile for context
            const userLocation = user?.address?.city || user?.city || ''; 
            const locationContext = userLocation ? `in or near ${userLocation}` : '';

            const prompt = `I am a party planner. I have a to-do item: "${task.text}" (Category: ${task.category}). 
            Please provide 3 short, specific, and actionable suggestions or sub-tasks to help me complete this ${locationContext}. 
            Format the response strictly as a JSON array of strings (e.g. ["Call 3 vendors", "Check reviews"]).`;

            const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=${GEMINI_API_KEY}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    contents: [{ role: "user", parts: [{ text: prompt }] }],
                    generationConfig: { responseMimeType: "application/json" }
                })
            });

            const result = await response.json();
            const text = result.candidates?.[0]?.content?.parts?.[0]?.text;
            
            if (text) {
                const suggestions = JSON.parse(text);
                await updateDoc(doc(db, collectionPath, task.id), {
                    suggestions: suggestions
                });
            }
        } catch (err) {
            console.error("AI Suggestions failed", err);
            alert("Could not get suggestions right now.");
        } finally {
            setSuggestingTaskId(null);
        }
    };

    // NEW: "Help Me Find It" Function
    const handleFindIt = async (e, task) => {
        e.preventDefault();
        e.stopPropagation();
        setFindingTaskId(task.id);

        try {
            // 1. Use AI to extract better search context (Category & Synonyms)
            let searchCategory = null;
            let keywords = [];
            
            try {
                const prompt = `Task: "${task.text}" (Category: ${task.category}). 
                1. Map this to ONE of these categories: ['venues', 'tableware', 'food', 'djs', 'mobile-bars', 'tents-canopies', 'tables-chairs', 'decorations', 'entertainment', 'audio-visual', 'photography', 'packages']. If unsure, use 'all'.
                2. Generate 5-8 specific search keywords/synonyms for finding listings for this task (e.g. if task is "find a place", keywords could be "venue", "hall", "event space").
                3. Return strictly JSON: { "category": "string", "keywords": ["string"] }`;

                const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=${GEMINI_API_KEY}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        contents: [{ role: "user", parts: [{ text: prompt }] }],
                        generationConfig: { responseMimeType: "application/json" }
                    })
                });

                const result = await response.json();
                const aiData = JSON.parse(result.candidates?.[0]?.content?.parts?.[0]?.text || '{}');
                
                searchCategory = aiData.category === 'all' ? null : aiData.category;
                keywords = aiData.keywords || [];
            } catch (err) {
                console.warn("AI Find It failed, using fallback", err);
                // Fallback: original basic mapping
                const catMap = { 'Venue': 'venues', 'Food & Drink': 'food', 'Decor': 'decorations', 'Music': 'djs', 'Logistics': 'tents-canopies' };
                searchCategory = catMap[task.category];
                keywords = task.text.toLowerCase().split(/\s+/).filter(w => w.length > 3);
            }

            // 2. Build Query
            let q;
            if (searchCategory) {
                q = query(collection(db, "listings"), where("category", "==", searchCategory), limit(20));
            } else {
                q = query(collection(db, "listings"), limit(50));
            }

            const snapshot = await getDocs(q);
            let candidates = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // 3. Filter by Date Availability (if due date exists)
            if (task.dueDate) {
                const dateString = new Date(task.dueDate).toISOString().split('T')[0];
                candidates = candidates.filter(item => {
                    if (!item.bookedDates || !item.bookedDates[dateString]) return true;
                    const bookedOnDate = item.bookedDates[dateString];
                    // Sum all booked options for this day to see if anything is left
                    const totalBooked = Object.values(bookedOnDate).reduce((a, b) => a + b, 0);
                    return totalBooked < (item.inventory || 1);
                });
            }

            // 4. Rank by Location (User City)
            const userCity = user?.address?.city || user?.city || '';
            if (userCity) {
                const cityLower = userCity.toLowerCase();
                candidates.sort((a, b) => {
                    const aLoc = (a.location || '').toLowerCase();
                    const bLoc = (b.location || '').toLowerCase();
                    const aMatch = aLoc.includes(cityLower);
                    const bMatch = bLoc.includes(cityLower);
                    return bMatch - aMatch; // True (1) first
                });
            }

            // 5. Keyword Match (Using AI keywords or original split)
            if (keywords.length > 0) {
                const searchTerms = keywords.map(k => k.toLowerCase());
                candidates = candidates.map(item => {
                    const content = (item.title + ' ' + item.description + ' ' + (item.category || '')).toLowerCase();
                    // Match if content includes any of the keywords
                    const matchScore = searchTerms.reduce((acc, k) => acc + (content.includes(k) ? 1 : 0), 0);
                    return { ...item, matchScore };
                }).filter(item => item.matchScore > 0).sort((a, b) => b.matchScore - a.matchScore);
            }

            const results = candidates.slice(0, 3).map(c => ({
                id: c.id,
                title: c.title,
                price: c.price,
                category: c.category,
                imageUrl: c.imageUrls?.[0] || ''
            }));

            if (results.length === 0) {
                alert("We couldn't find any specific matches for this task in your area.");
            } else {
                await updateDoc(doc(db, collectionPath, task.id), {
                    foundListings: results
                });
            }

        } catch (err) {
            console.error("Find It failed:", err);
            alert("Could not search listings right now.");
        } finally {
            setFindingTaskId(null);
        }
    };

    const getPriorityColor = (p) => {
        switch(p) {
            case 'High': return 'bg-red-100 text-red-800 border-red-200';
            case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'Low': return 'bg-blue-100 text-blue-800 border-blue-200';
            default: return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const filteredTasks = tasks.filter(t => {
        if (filter === 'active') return !t.completed;
        if (filter === 'completed') return t.completed;
        return true;
    });

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg h-full flex flex-col">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h3 className="text-xl font-bold text-gray-800">{eventId ? 'Event Checklist' : 'General Checklist'}</h3>
                    <p className="text-sm text-gray-500">Track tasks, deadlines, and priorities.</p>
                </div>
                <div className="flex bg-gray-100 p-1 rounded-lg">
                    {['all', 'active', 'completed'].map(f => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`px-3 py-1 rounded-md text-xs font-semibold capitalize transition ${filter === f ? 'bg-white shadow text-pink-600' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            {f}
                        </button>
                    ))}
                </div>
            </div>
            
            <form onSubmit={addTask} className="mb-6 bg-gray-50 p-4 rounded-xl border border-gray-100">
                <div className="flex gap-2 mb-3">
                    <input
                        type="text"
                        value={newTask}
                        onChange={(e) => setNewTask(e.target.value)}
                        placeholder="What needs to be done?"
                        className="flex-grow p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none"
                    />
                </div>
                <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                    <div className="flex-1 min-w-[120px]">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Category</label>
                        <select value={category} onChange={e => setCategory(e.target.value)} className="w-full p-2 text-sm border rounded-lg bg-white">
                            {categories.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div className="flex-1 min-w-[100px]">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Priority</label>
                        <select value={priority} onChange={e => setPriority(e.target.value)} className="w-full p-2 text-sm border rounded-lg bg-white">
                            <option value="High">High Priority</option>
                            <option value="Medium">Medium</option>
                            <option value="Low">Low</option>
                        </select>
                    </div>
                    <div className="flex-1 min-w-[130px]">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Due Date</label>
                        <input 
                            type="date" 
                            value={dueDate} 
                            onChange={e => setDueDate(e.target.value)} 
                            className="w-full p-2 text-sm border rounded-lg bg-white focus:ring-2 focus:ring-pink-500 outline-none" 
                        />
                    </div>
                    <div className="flex-1 min-w-[130px]">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Reminder</label>
                        <input 
                            type="date" 
                            value={reminderDate} 
                            onChange={e => setReminderDate(e.target.value)} 
                            className="w-full p-2 text-sm border rounded-lg bg-white focus:ring-2 focus:ring-pink-500 outline-none text-gray-500" 
                        />
                    </div>
                    <div className="flex-1 min-w-[130px]">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Assignee</label>
                        <input
                            type="text"
                            value={assignedTo}
                            onChange={e => setAssignedTo(e.target.value)}
                            placeholder="Assign to..."
                            className="w-full p-2 text-sm border rounded-lg bg-white focus:ring-2 focus:ring-pink-500 outline-none"
                        />
                    </div>
                    <div className="w-full sm:w-auto flex items-end">
                        <button type="submit" className="bg-pink-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-pink-600 transition shadow-md w-full h-[38px]">
                            Add Task
                        </button>
                    </div>
                </div>
            </form>

            <div className="space-y-3 overflow-y-auto pr-2 custom-scrollbar flex-grow" style={{ maxHeight: '500px' }}>
                {filteredTasks.length > 0 ? filteredTasks.map(task => (
                    <div key={task.id} className={`p-4 rounded-lg border transition-all duration-200 group ${task.completed ? 'bg-gray-50 border-gray-200 opacity-75' : 'bg-white border-gray-200 hover:border-pink-300 hover:shadow-sm'}`}>
                        {editingTask?.id === task.id ? (
                            // --- NEW: Edit Mode UI ---
                            <div className="flex flex-col gap-3 w-full animate-fade-in">
                                <input 
                                    type="text" 
                                    value={editingTask.text} 
                                    onChange={(e) => setEditingTask({...editingTask, text: e.target.value})} 
                                    className="w-full p-2 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                    autoFocus
                                />
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
                                     <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Category</label>
                                        <select value={editingTask.category} onChange={(e) => setEditingTask({...editingTask, category: e.target.value})} className="w-full p-1.5 border rounded text-xs bg-white focus:ring-2 focus:ring-blue-500 outline-none">
                                            {categories.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Priority</label>
                                        <select value={editingTask.priority} onChange={(e) => setEditingTask({...editingTask, priority: e.target.value})} className="w-full p-1.5 border rounded text-xs bg-white focus:ring-2 focus:ring-blue-500 outline-none">
                                            <option value="High">High</option>
                                            <option value="Medium">Medium</option>
                                            <option value="Low">Low</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Due Date</label>
                                        <input 
                                            type="date" 
                                            value={editingTask.dueDate} 
                                            onChange={(e) => setEditingTask({...editingTask, dueDate: e.target.value})} 
                                            className="w-full p-1.5 border rounded text-xs bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Reminder</label>
                                        <input 
                                            type="date" 
                                            value={editingTask.reminderDate || ''} 
                                            onChange={(e) => setEditingTask({...editingTask, reminderDate: e.target.value})} 
                                            className="w-full p-1.5 border rounded text-xs bg-white focus:ring-2 focus:ring-blue-500 outline-none text-gray-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Assignee</label>
                                        <input 
                                            type="text" 
                                            placeholder="Assign to..." 
                                            value={editingTask.assignedTo || ''} 
                                            onChange={(e) => setEditingTask({...editingTask, assignedTo: e.target.value})} 
                                            className="w-full p-1.5 border rounded text-xs bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                                        />
                                    </div>
                                </div>
                                <div className="flex gap-2 justify-end border-t pt-2 mt-1">
                                    <button onClick={() => setEditingTask(null)} className="px-3 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-100 rounded">Cancel</button>
                                    <button onClick={saveEdit} className="px-3 py-1 text-xs font-bold text-white bg-blue-500 hover:bg-blue-600 rounded shadow-sm">Save Changes</button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-start gap-4">
                                <div onClick={() => toggleTask(task)} className={`mt-1 w-6 h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center cursor-pointer transition-colors ${task.completed ? 'bg-green-500 border-green-500' : 'border-gray-300 group-hover:border-pink-400 bg-white'}`}>
                                    {task.completed && <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>}
                                </div>
                                
                                <div className="flex-grow min-w-0">
                                    <div className="flex justify-between items-start">
                                        <span className={`text-lg font-medium transition-colors break-words ${task.completed ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                                            {task.text}
                                        </span>
                                        <div className="flex gap-1">
                                            {/* NEW: Edit Button */}
                                            <button 
                                                type="button" 
                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditingTask({...task}); }} 
                                                className="text-gray-400 hover:text-blue-500 p-1 transition-colors" 
                                                title="Edit"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                                </svg>
                                            </button>
                                            <button 
                                                type="button" 
                                                onClick={(e) => getSuggestions(e, task)} 
                                                disabled={suggestingTaskId === task.id}
                                                className={`p-1 transition-colors ${suggestingTaskId === task.id ? 'text-purple-400 animate-pulse' : 'text-gray-400 hover:text-purple-500'}`} 
                                                title="Get AI Suggestions"
                                            >
                                                {suggestingTaskId === task.id ? (
                                                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                                ) : (
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
                                                )}
                                            </button>
                                            {/* NEW: Find It Button */}
                                            <button 
                                                type="button" 
                                                onClick={(e) => handleFindIt(e, task)} 
                                                disabled={findingTaskId === task.id}
                                                className={`p-1 transition-colors ${findingTaskId === task.id ? 'text-blue-400 animate-pulse' : 'text-gray-400 hover:text-blue-500'}`} 
                                                title="Find items for this task"
                                            >
                                                {findingTaskId === task.id ? (
                                                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                                ) : (
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                                                )}
                                            </button>
                                            <button type="button" onClick={(e) => deleteTask(e, task.id)} className="text-gray-400 hover:text-red-500 p-1 transition-colors" title="Delete">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>

                                    {/* NEW: Render Found Listings */}
                                    {task.foundListings && task.foundListings.length > 0 && (
                                        <div className="mt-2 ml-1 bg-blue-50 p-2 rounded-lg border border-blue-100 animate-fade-in">
                                            <p className="text-xs font-bold text-blue-800 mb-2 flex items-center gap-1">
                                                <span>🔍</span> We found these for you:
                                            </p>
                                            <div className="space-y-2">
                                                {task.foundListings.map((item) => (
                                                    <div 
                                                        key={item.id} 
                                                        onClick={() => navigateTo('listingDetail', { id: item.id })}
                                                        className="flex items-center gap-2 bg-white p-1.5 rounded border border-blue-100 hover:shadow-sm cursor-pointer transition"
                                                    >
                                                        <img src={item.imageUrl || 'https://placehold.co/40x40/e2e8f0/64748b?text=Img'} alt={item.title} className="w-8 h-8 rounded object-cover" />
                                                        <div className="flex-grow min-w-0">
                                                            <p className="text-xs font-semibold text-gray-800 truncate">{item.title}</p>
                                                            <p className="text-[10px] text-green-600 font-medium">${item.price}</p>
                                                        </div>
                                                        <span className="text-blue-500 text-xs px-2">View &rarr;</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {task.suggestions && task.suggestions.length > 0 && (
                                        <div className="mt-2 ml-1 bg-purple-50 p-2 rounded-lg border border-purple-100 animate-fade-in">
                                            <p className="text-xs font-bold text-purple-800 mb-1 flex items-center gap-1">
                                                <span>✨</span> AI Suggestions
                                            </p>
                                            <ul className="list-disc list-inside text-xs text-purple-700 space-y-1">
                                                {task.suggestions.map((suggestion, idx) => (
                                                    <li key={idx}>{suggestion}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    
                                    <div className="flex flex-wrap items-center gap-2 mt-2">
                                        {task.category && (
                                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                                                {task.category}
                                            </span>
                                        )}
                                        {task.priority && (
                                            <span className={`px-2 py-0.5 rounded text-xs font-bold border ${getPriorityColor(task.priority)}`}>
                                                {task.priority}
                                            </span>
                                        )}
                                        {task.dueDate && (
                                            <span className={`px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1 ${new Date(task.dueDate) < new Date() && !task.completed ? 'text-red-600 bg-red-50' : 'text-gray-500 bg-gray-50'}`}>
                                                <span>📅</span> {new Date(task.dueDate).toLocaleDateString(undefined, {month:'short', day:'numeric'})}
                                            </span>
                                        )}
                                        {task.reminderDate && (
                                            <span className={`px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1 ${new Date(task.reminderDate) <= new Date() && !task.completed ? 'text-orange-600 bg-orange-50 border border-orange-200 animate-pulse' : 'text-gray-500 bg-gray-50 border border-gray-200'}`} title="Reminder">
                                                <span>🔔</span> {new Date(task.reminderDate).toLocaleDateString(undefined, {month:'short', day:'numeric'})}
                                            </span>
                                        )}
                                        {task.assignedTo && (
                                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200 flex items-center gap-1">
                                                <span>👤</span> {task.assignedTo}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )) : (
                    <div className="text-center py-12 flex flex-col items-center justify-center text-gray-400 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50">
                        <div className="text-4xl mb-2">📝</div>
                        <p>No tasks found. Add one to get started!</p>
                    </div>
                )}
            </div>
        </div>
    );
}